import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  embalagens as api,
  type EmbalagemDaFamilia,
  type MultiplicadorDoFrete,
} from '../api/embalagens'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'
import { formatar } from '../i18n'
import './usuarios.css'

/**
 * A caixa em que o material viaja, e quantos quilos cabem nela (demanda 076).
 *
 * 🔴 **O parâmetro tinha leitor e não tinha escritor.** O motor de frete lê
 * `frete.dimensao_padrao.*` desde sempre; nenhuma tela escrevia. É a quinta
 * ocorrência dessa família no projeto — e a primeira em que o campo sem escritor
 * **bloqueia uma funcionalidade inteira** em vez de só exibir um estado errado.
 *
 * ⭐ **Gravar aqui é o ato de conferência.** A fronteira do frete pergunta se um
 * humano da Rio DG olhou para aquele número — ninguém digita `22x22x32/8` por
 * acidente. Enquanto a linha estiver "de fábrica", o assistente **não cota** e
 * manda para uma pessoa.
 */
export function Embalagens() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const editavel = pode('ParametroGerenciar')

  const [linhas, setLinhas] = useState<EmbalagemDaFamilia[]>([])
  const [valores, setValores] = useState<Record<string, string>>({})
  const [fator, setFator] = useState<MultiplicadorDoFrete | null>(null)
  const [fatorDigitado, setFatorDigitado] = useState('')
  const [carregando, setCarregando] = useState(true)
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const lidas = await api.listar()

      setLinhas(lidas)
      setValores(Object.fromEntries(lidas.map((l) => [l.familia, l.valor ?? ''])))

      // 🔴 **Em separado, e tolerando falha** (demanda 161). A primeira versão
      // pedia os dois num `Promise.all`, e o CI mostrou o custo disso: a rota
      // nova falhando derrubava a tela INTEIRA — inclusive o cadastro de
      // embalagem, que é o que destrava o frete.
      //
      // ⭐ O multiplicador é complementar; a embalagem é o que trava. Quem
      // manda na tela é a segunda.
      try {
        const multiplicador = await api.multiplicador()
        setFator(multiplicador)

        // 🔴 **O campo mostra o GRAVADO, não o efetivo** (revisão do PR 60).
        // Com um valor recusado no banco — `12`, digamos —, o efetivo volta
        // `1,2000`; exibir isso faria quem tentasse corrigir digitar o mesmo
        // texto que já está na tela, e a comparação concluiria que nada mudou.
        setFatorDigitado(multiplicador.gravado ?? multiplicador.valor)
      } catch {
        setFator(null)
      }
    } catch {
      setFalhou(true)
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function salvar(e: FormEvent) {
    e.preventDefault()

    setErro(null)
    setRecado(null)
    setEnviando(true)

    try {
      // Só o que mudou. Mandar tudo geraria auditoria de linha que ninguém
      // tocou, e a pergunta que a auditoria responde é "quem mudou o quê".
      const mudadas = linhas.filter((l) => (valores[l.familia] ?? '') !== (l.valor ?? ''))

      for (const linha of mudadas) {
        const valor = (valores[linha.familia] ?? '').trim()
        await api.gravar(linha.familia, valor === '' ? null : valor)
      }

      // ⚠️ Comparado com o que veio do servidor, e não com o que está na tela:
      // gravar sem mudança criaria linha de auditoria de um número que ninguém
      // tocou. E a comparação é contra o **gravado** — com o efetivo, um valor
      // recusado seria impossível de corrigir (revisão do PR 60).
      const fatorMudou =
        fator !== null && fatorDigitado.trim() !== (fator.gravado ?? '')

      if (fatorMudou) await api.gravarMultiplicador(fatorDigitado.trim() || null)

      // ⚠️ **Recados separados** (revisão do PR 60): `embalagens.salvo` diz
      // "N embalagem(ns) gravada(s)", e somar o multiplicador ali faria a tela
      // afirmar que uma embalagem foi gravada quando nenhuma foi.
      const recados = [
        mudadas.length > 0 ? t('embalagens.salvo', { total: mudadas.length }) : null,
        fatorMudou ? t('embalagens.multiplicadorSalvo') : null,
      ].filter(Boolean)

      setRecado(recados.length === 0 ? t('embalagens.nadaMudou') : recados.join(' '))

      await carregar()
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) return <Estado tipo="carregando" />
  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  const semPadrao = linhas.find((l) => l.familia === 'padrao')?.deFabrica ?? true

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('embalagens.titulo')}</h1>
          <p className="usuarios__descricao">{t('embalagens.descricao')}</p>
        </div>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {recado && <Aviso tom="sucesso">{recado}</Aviso>}
      {!editavel && <Aviso tom="info">{t('assistentes.somenteLeitura')}</Aviso>}

      {/* 🔴 Sem o padrão geral cadastrado, o assistente NÃO cota frete para
          ninguém. É o estado inicial do sistema, e ele precisa ser dito — senão
          quem abre a tela não sabe que a ausência é o que trava. */}
      {semPadrao && <Aviso tom="alerta">{t('embalagens.semPadrao')}</Aviso>}

      <form onSubmit={salvar} noValidate>
        {/* 🔴 **O multiplicador do frete** (demanda 161 · decisão D5 de 02/09):
            20% sobre o valor cotado, para cobrir a bitributação.

            ⚠️ A vírgula é obrigatória, e o campo diz isso. Em pt-BR o ponto é
            separador de MILHAR: `1.2` lido sem cuidado viraria 12, e o frete
            sairia doze vezes maior. O back recusa o que não entende — mas quem
            digita precisa saber antes, não depois. */}
        <Cartao titulo={t('embalagens.multiplicador')}>
          {fator === null ? (
            // ⚠️ Diz que não leu, em vez de mostrar um campo vazio que pareceria
            // "sem acréscimo". Campo em branco aqui seria lido como zero por cento.
            <Aviso tom="alerta">{t('embalagens.multiplicadorIndisponivel')}</Aviso>
          ) : (
            <>
              <Aviso tom="info">
                {t('embalagens.multiplicadorAjuda', {
                  minimo: fator.minimo,
                  maximo: fator.maximo,
                })}
              </Aviso>

              <label className="usuarios__campo">
                <span>{t('embalagens.multiplicadorValor')}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fatorDigitado}
                  placeholder="1,2000"
                  disabled={!editavel}
                  onChange={(e) => setFatorDigitado(e.target.value)}
                />
              </label>

              <p className="usuarios__descricao">
                {fator.deFabrica
                  ? t('embalagens.multiplicadorDeFabrica')
                  : t('embalagens.gravadoPor', {
                      quem: fator.atualizadoPor ?? '—',
                      quando: fator.atualizadoEm ? formatar.data(fator.atualizadoEm) : '—',
                    })}
              </p>
            </>
          )}
        </Cartao>

        <Cartao titulo={t('embalagens.tabela')}>
          <Aviso tom="info">{t('embalagens.formatoAjuda')}</Aviso>

          <div className="usuarios__tabela-rolagem" tabIndex={0}>
            <table className="usuarios__tabela">
              <thead>
                <tr>
                  <th scope="col">{t('embalagens.familia')}</th>
                  <th scope="col">{t('embalagens.valor')}</th>
                  <th scope="col">{t('embalagens.origem')}</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((linha) => (
                  <tr key={linha.familia}>
                    <th scope="row">
                      {linha.familia === 'padrao' ? t('embalagens.padraoGeral') : linha.familia}
                    </th>
                    <td>
                      <input
                        type="text"
                        inputMode="text"
                        value={valores[linha.familia] ?? ''}
                        placeholder={
                          linha.familia === 'padrao'
                            ? '22x22x32/8'
                            : t('embalagens.usaOPadrao')
                        }
                        aria-label={t('embalagens.valorDe', { familia: linha.familia })}
                        disabled={!editavel}
                        onChange={(e) =>
                          setValores((v) => ({ ...v, [linha.familia]: e.target.value }))
                        }
                      />
                    </td>
                    <td>
                      {linha.deFabrica ? (
                        <span className="usuarios__etiqueta">{t('embalagens.deFabrica')}</span>
                      ) : (
                        <span>
                          {t('embalagens.gravadoPor', {
                            quem: linha.atualizadoPor ?? '—',
                            quando: linha.atualizadoEm
                              ? formatar.data(linha.atualizadoEm)
                              : '—',
                          })}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Cartao>

        {editavel && (
          <Botao type="submit" carregando={enviando}>
            {t('comum.salvar')}
          </Botao>
        )}
      </form>
    </main>
  )
}
